// Decides which badge fields print for an attendee and closes the gaps left by the ones that don't.

// Fields further apart than this belong to separate blocks (e.g. a footer) and never move together.
const STACK_GAP_IN = 0.5;
const EPSILON = 0.01;

const isEmpty = (value) => value === null || value === undefined || String(value).trim() === '';

const overlapsHorizontally = (a, b) => a.x < b.x + b.width - EPSILON && b.x < a.x + a.width - EPSILON;

/** Category of the attendee, from a top-level or form "category" key (any casing). */
export function badgeCategory(userData) {
  if (!userData) return null;
  const key = Object.keys(userData).find((k) => k.toLowerCase() === 'category');
  const value = key ? userData[key] : null;
  return isEmpty(value) ? null : String(value).trim();
}

/** Options of the event form's "Category" field, for choosing per-category visibility. */
export function categoryOptions(formSchema = []) {
  const field = (formSchema || []).find((f) => /^category$/i.test(String(f?.label || f?.name || '').trim()))
    || (formSchema || []).find((f) => /category/i.test(String(f?.label || f?.name || '')));
  if (!field) return [];
  const labels = field.optionsWithPricing?.map((o) => o?.label) || field.options || [];
  return [...new Set(labels.filter((label) => label && String(label).trim()))];
}

export const hiddenCategoriesOf = (field) => (Array.isArray(field?.properties?.hiddenForCategories)
  ? field.properties.hiddenForCategories : []);

export function isHiddenForCategory(field, category) {
  if (!category) return false;
  const wanted = category.toLowerCase();
  return hiddenCategoriesOf(field).some((c) => String(c).trim().toLowerCase() === wanted);
}

/**
 * Returns Map(field -> { value, y }) for the fields to print. Text fields with no value and fields
 * hidden for the attendee's category are dropped, and fields stacked below them move up by the space
 * the dropped field took (its top to the top of the next field in the stack).
 */
export function resolveBadgeLayout(fields = [], { valueOf, category = null } = {}) {
  const active = fields.filter((field) => field && field.visible !== false)
    .map((field) => ({ field, value: valueOf(field) }));
  const removed = (entry) => isHiddenForCategory(entry.field, category)
    || (entry.field.type === 'text' && isEmpty(entry.value));

  const byTop = [...active].sort((a, b) => a.field.y - b.field.y);
  const shift = new Map(active.map((entry) => [entry.field, 0]));

  byTop.filter(removed).forEach((gone) => {
    const column = byTop.filter((entry) => entry !== gone
      && entry.field.y > gone.field.y + EPSILON && overlapsHorizontally(entry.field, gone.field));
    const next = column[0];
    if (!next || next.field.y - (gone.field.y + gone.field.height) > STACK_GAP_IN) return;

    const slot = next.field.y - gone.field.y;
    let stackBottom = next.field.y + next.field.height;
    for (const entry of column) {
      if (entry !== next) {
        if (entry.field.y - stackBottom > STACK_GAP_IN) break;
        stackBottom = Math.max(stackBottom, entry.field.y + entry.field.height);
      }
      shift.set(entry.field, shift.get(entry.field) + slot);
    }
  });

  const placed = new Map();
  active.filter((entry) => !removed(entry)).forEach(({ field, value }) => {
    placed.set(field, { value, y: field.y - shift.get(field) });
  });
  return placed;
}
